/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { PageShopIndexResponse } from '../models/PageShopIndexResponse';
import type { ReservedShopSplitPaymentApproveForm } from '../models/ReservedShopSplitPaymentApproveForm';
import type { ShopDetailResponse } from '../models/ShopDetailResponse';
import type { ShopEditForm } from '../models/ShopEditForm';
import type { ShopSearchForm } from '../models/ShopSearchForm';
import type { ShopSplitPaymentDetailResponse } from '../models/ShopSplitPaymentDetailResponse';
import type { ShopSplitPaymentEditForm } from '../models/ShopSplitPaymentEditForm';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class ShopRepository {

  /**
   * 店舗一覧表示
   * @returns PageShopIndexResponse OK
   * @throws ApiError
   */
  public static index({
    shopIds,
    externalShopIds,
    statuses,
    shopPhoneNumber,
    acquirerIds,
    companyIds,
    issuerIds,
    walletServiceIds,
    createdAtStartDateTime,
    createdAtEndDateTime,
    giftBusinessIds,
    giftTicketIds,
    page = 1,
    size = 20,
    sort,
  }: {
    /**
     * 店舗(複数指定可能)
     */
    shopIds: Array<string> | undefined,
    /**
     * 外部店舗ID（複数指定可能）
     */
    externalShopIds: Array<string> | undefined,
    /**
     *
     * ステータス(複数指定可能)
     *
     * ※利用開始日、利用終了日、現在日時の比較でアプリ側で設定
     *
     * | ステータス | 条件 |
     * | --- | --- |
     * | 有効待ち | 現在日時 < 利用開始日 |
     * | 有効 | 利用開始日 < 現在日時 < 利用終了日 |
     * | 終了 | 利用終了日 < 現在日時 |
     *
     */
    statuses: Array<'WAITING' | 'AVAILABLE' | 'CLOSED'> | undefined,
    /**
     * 店舗電話番号（複数指定不可）
     */
    shopPhoneNumber: string | undefined,
    /**
     * アクワイアラ名(複数指定可能)
     */
    acquirerIds: Array<string> | undefined,
    /**
     * 企業名(複数指定可能)
     */
    companyIds: Array<string> | undefined,
    /**
     * イシュア名(複数指定可能)
     */
    issuerIds: Array<string> | undefined,
    /**
     * ウォレットサービス名(複数指定可能)
     */
    walletServiceIds: Array<string> | undefined,
    /**
     * 登録日時(開始日)　※msまでの日時が検索に反映される
     */
    createdAtStartDateTime: string | undefined,
    /**
     * 登録日時(終了日)　※msまでの日時が検索に反映される
     */
    createdAtEndDateTime: string | undefined,
    /**
     * 商品券事業ID（複数指定可能）
     */
    giftBusinessIds: Array<number> | undefined,
    /**
     * 商品券面ID（複数指定可能）
     */
    giftTicketIds: Array<number> | undefined,
    /**
     * One-based page index (1..N)
     */
    page: number | undefined,
    /**
     * The size of the page to be returned
     */
    size: number | undefined,
    /**
     * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
     */
    sort: Array<string> | undefined,
  }): CancelablePromise<PageShopIndexResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/shop',
      query: {
        'shopIds': shopIds,
        'externalShopIds': externalShopIds,
        'statuses': statuses,
        'shopPhoneNumber': shopPhoneNumber,
        'acquirerIds': acquirerIds,
        'companyIds': companyIds,
        'issuerIds': issuerIds,
        'walletServiceIds': walletServiceIds,
        'createdAtStartDateTime': createdAtStartDateTime,
        'createdAtEndDateTime': createdAtEndDateTime,
        'giftBusinessIds': giftBusinessIds,
        'giftTicketIds': giftTicketIds,
        'page': page,
        'size': size,
        'sort': sort,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * 店舗一覧Csvを生成、ストレージにUploadし、ダウンロードするための署名付きURLを返却する
   * @returns any OK
   * @throws ApiError
   */
  public static downloadShop({
    requestBody,
  }: {
    requestBody: ShopSearchForm,
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/shop/download',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * 店舗詳細表示
   * @returns ShopDetailResponse OK
   * @throws ApiError
   */
  public static detail({
    id,
  }: {
    /**
     * 店舗ID
     */
    id: string,
  }): CancelablePromise<ShopDetailResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/shop/{id}',
      path: {
        'id': id,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * 店舗情報更新
   * @returns any OK
   * @throws ApiError
   */
  public static edit({
    id,
    requestBody,
  }: {
    id: string,
    requestBody: ShopEditForm,
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/shop/{id}',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * 店舗分割決済情報 予約された編集内容を承認する
   * @returns any OK
   * @throws ApiError
   */
  public static approve({
    id,
    reservedShopSplitPaymentInformationId,
    requestBody,
  }: {
    id: string,
    reservedShopSplitPaymentInformationId: string,
    requestBody: ReservedShopSplitPaymentApproveForm,
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/shop/{id}/reserved_split_payment/{reservedShopSplitPaymentInformationId}/approve',
      path: {
        'id': id,
        'reservedShopSplitPaymentInformationId': reservedShopSplitPaymentInformationId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * 店舗分割決済情報 予約された編集内容を却下する
   * @returns any OK
   * @throws ApiError
   */
  public static reject({
    id,
    reservedShopSplitPaymentInformationId,
    requestBody,
  }: {
    id: string,
    reservedShopSplitPaymentInformationId: string,
    requestBody: ReservedShopSplitPaymentApproveForm,
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/shop/{id}/reserved_split_payment/{reservedShopSplitPaymentInformationId}/reject',
      path: {
        'id': id,
        'reservedShopSplitPaymentInformationId': reservedShopSplitPaymentInformationId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * 店舗分割決済情報 予約された編集内容を取下する
   * @returns any OK
   * @throws ApiError
   */
  public static withdraw({
    id,
    reservedShopSplitPaymentInformationId,
    requestBody,
  }: {
    id: string,
    reservedShopSplitPaymentInformationId: string,
    requestBody: ReservedShopSplitPaymentApproveForm,
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/shop/{id}/reserved_split_payment/{reservedShopSplitPaymentInformationId}/withdraw',
      path: {
        'id': id,
        'reservedShopSplitPaymentInformationId': reservedShopSplitPaymentInformationId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * 店舗分割決済情報 詳細表示
   * @returns ShopSplitPaymentDetailResponse OK
   * @throws ApiError
   */
  public static detail1({
    id,
  }: {
    /**
     * 店舗ID
     */
    id: string,
  }): CancelablePromise<ShopSplitPaymentDetailResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/shop/{id}/split_payment',
      path: {
        'id': id,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * 店舗分割決済情報 更新
   * @returns any OK
   * @throws ApiError
   */
  public static edit1({
    id,
    requestBody,
  }: {
    id: string,
    requestBody: ShopSplitPaymentEditForm,
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/shop/{id}/split_payment',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

}
